/**
 * Error handling utilities for user-friendly error messages.
 *
 * Provides:
 * - ApiError class that extracts detailed error info from API responses
 * - Polish translations for common error messages
 * - Field-level error extraction from various API response formats
 * - getErrorMessage() utility for displaying errors to users
 */

/**
 * Generic interface to handle various API error formats.
 */
export interface ApiErrorResponse {
  // Common patterns from different APIs:
  error?: string; // { error: "message" }
  message?: string; // { message: "message" }
  msg?: string; // { msg: "message" }
  errors?: unknown[]; // { errors: [...] }
  details?: unknown; // { details: [...] } (Zod format) or string (server error message)
  [key: string]: unknown; // Allow any other fields
}

export interface FieldError {
  field: string;
  message: string;
}

/**
 * Format technical details from various response formats.
 * Handles:
 * - String: "UNIQUE constraint failed" → returns as-is
 * - Zod array: [{ path: ["nrVat"], message: "Required" }] → "nrVat: Required"
 * - Other arrays: ["error1", "error2"] → "error1, error2"
 */
function formatTechnicalDetails(details: unknown): string | undefined {
  if (typeof details === "string") {
    return details;
  }

  if (Array.isArray(details) && details.length > 0) {
    // Check if it's Zod error format: [{ path: [...], message: "..." }]
    const firstItem = details[0];
    if (
      firstItem &&
      typeof firstItem === "object" &&
      "path" in firstItem &&
      "message" in firstItem
    ) {
      // Zod validation errors
      return details
        .map((item: { path?: unknown[]; message?: string }) => {
          const field =
            Array.isArray(item.path) && item.path.length > 0
              ? String(item.path[0])
              : "";
          const message = item.message || "";
          return field ? `${field}: ${message}` : message;
        })
        .join("; ");
    }

    // Generic array - join with comma
    return details.map(String).join(", ");
  }

  return undefined;
}

/**
 * Custom error class for API errors with detailed information.
 */
export class ApiError extends Error {
  public readonly technicalDetails?: string;

  constructor(
    public readonly status: number,
    public readonly response: ApiErrorResponse,
    public readonly fieldErrors: FieldError[] = []
  ) {
    super(extractMainMessage(response, status));
    this.name = "ApiError";
    this.technicalDetails = formatTechnicalDetails(response.details);
  }

  /**
   * Get a formatted, user-friendly error message in Polish.
   */
  getFormattedMessage(): string {
    if (this.fieldErrors.length > 0) {
      return this.fieldErrors
        .slice(0, 3) // Limit to 3 field errors
        .map(
          (e) =>
            `${translateFieldName(e.field)}: ${translateFieldError(e.message)}`
        )
        .join(", ");
    }
    return translateError(this.message, this.status);
  }
}

/**
 * Extract main error message from various response formats.
 */
function extractMainMessage(
  response: ApiErrorResponse,
  status: number
): string {
  // Try common patterns
  if (typeof response.error === "string") return response.error;
  if (typeof response.message === "string") return response.message;
  if (typeof response.msg === "string") return response.msg;

  // Fallback to status-based message
  return getStatusMessage(status);
}

/**
 * Extract field errors from various API response formats.
 */
function extractFieldErrors(response: ApiErrorResponse): FieldError[] {
  const errors: FieldError[] = [];

  // Format: { details: [{ path: ["field"], message: "..." }] } (Zod)
  if (Array.isArray(response.details)) {
    for (const item of response.details) {
      if (item && typeof item === "object") {
        const path = (item as { path?: unknown }).path;
        const message = (item as { message?: unknown }).message;
        if (
          Array.isArray(path) &&
          path.length > 0 &&
          typeof message === "string"
        ) {
          errors.push({ field: String(path[0]), message });
        }
      }
    }
  }

  // Format: { errors: [{ field: "name", message: "..." }] } (Express-validator style)
  if (Array.isArray(response.errors)) {
    for (const item of response.errors) {
      if (item && typeof item === "object") {
        const itemObj = item as Record<string, unknown>;
        const field = itemObj.field || itemObj.name || itemObj.param;
        const message = itemObj.message || itemObj.msg;
        if (typeof field === "string" && typeof message === "string") {
          errors.push({ field, message });
        }
      }
    }
  }

  // Format: { fieldName: "error message" } or { fieldName: ["error1", "error2"] }
  const reservedKeys = [
    "error",
    "message",
    "msg",
    "details",
    "errors",
    "status",
    "statusCode",
  ];
  for (const [key, value] of Object.entries(response)) {
    if (reservedKeys.includes(key)) continue;
    if (typeof value === "string") {
      errors.push({ field: key, message: value });
    } else if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "string"
    ) {
      errors.push({ field: key, message: value[0] });
    }
  }

  return errors;
}

/**
 * Get Polish status message based on HTTP status code.
 */
function getStatusMessage(status: number): string {
  switch (status) {
    case 400:
      return "Nieprawidłowe dane";
    case 401:
      return "Wymagane logowanie";
    case 403:
      return "Brak dostępu";
    case 404:
      return "Nie znaleziono";
    case 409:
      return "Konflikt danych";
    case 422:
      return "Błąd walidacji";
    case 500:
      return "Błąd serwera";
    default:
      return "Wystąpił błąd";
  }
}

/**
 * Translate common error messages to Polish.
 */
function translateError(message: string, status: number): string {
  const lowerMsg = message.toLowerCase();

  // Match patterns rather than exact strings
  if (lowerMsg.includes("invalid") && lowerMsg.includes("data"))
    return "Nieprawidłowe dane";
  if (lowerMsg.includes("not found")) return "Nie znaleziono";
  if (lowerMsg.includes("already exists")) return "Już istnieje";
  if (lowerMsg.includes("unauthorized")) return "Brak autoryzacji";
  if (lowerMsg.includes("forbidden")) return "Brak dostępu";
  if (lowerMsg.includes("required")) return "Brakujące dane";
  if (lowerMsg.includes("validation") && lowerMsg.includes("failed"))
    return "Błąd walidacji";
  if (lowerMsg.includes("duplicate")) return "Duplikat";
  if (lowerMsg.includes("conflict")) return "Konflikt danych";

  // If no pattern matches, use status-based fallback
  return getStatusMessage(status);
}

/**
 * Translate field-level error messages to Polish.
 */
function translateFieldError(message: string): string {
  const lowerMsg = message.toLowerCase();

  if (lowerMsg === "required" || lowerMsg.includes("is required"))
    return "Pole wymagane";
  if (lowerMsg.includes("expected string")) return "Oczekiwano tekstu";
  if (lowerMsg.includes("expected number")) return "Oczekiwano liczby";
  if (lowerMsg.includes("invalid email")) return "Nieprawidłowy email";
  if (lowerMsg.includes("too short") || lowerMsg.includes("at least"))
    return "Za krótkie";
  if (lowerMsg.includes("too long")) return "Za długie";
  if (lowerMsg.includes("invalid")) return "Nieprawidłowa wartość";
  if (lowerMsg.includes("must be")) return "Nieprawidłowa wartość";
  if (lowerMsg.includes("cannot be empty")) return "Pole wymagane";
  if (lowerMsg.includes("not a valid")) return "Nieprawidłowa wartość";

  return message; // Return original if no translation
}

/**
 * Polish translations for common field names.
 */
const FIELD_NAMES: Record<string, string> = {
  nazwa: "Nazwa",
  nip: "NIP",
  nrVat: "Nr VAT",
  nrFvat: "Nr faktury",
  netto: "Netto",
  brutto: "Brutto",
  vat: "VAT",
  email: "Email",
  telefon: "Telefon",
  adresSiedziby: "Adres",
  opis: "Opis",
  kwotaNetto: "Kwota netto",
  kwotaBrutto: "Kwota brutto",
  kwotaVat: "Kwota VAT",
  nazwaDostawcy: "Nazwa dostawcy",
  nipDostawcy: "NIP dostawcy",
  dataZaplaty: "Data zapłaty",
  terminPlatnosci: "Termin płatności",
  dataWystawienia: "Data wystawienia",
  klient: "Klient",
  nrPorzadkowyProjektu: "Nr projektu",
  nazwaProjektu: "Nazwa projektu",
  typKlienta: "Typ klienta",
  priorytet: "Priorytet",
  typProjektu: "Typ projektu",
  statusProjektu: "Status projektu",
  rodzajProjektu: "Rodzaj projektu",
  nazwaRoboczaProjektu: "Nazwa projektu",
  wartoscUmowyNetto: "Wartość umowy netto",
  zaplacono: "Zapłacono",
  czyZaplacono: "Czy zapłacono",
  pozostalo: "Pozostało",
  rodzaj: "Rodzaj",
  nrPorzadkowyKlienta: "Nr klienta",
  nazwaKlienta: "Nazwa klienta",
  nipKlienta: "NIP klienta",
};

/**
 * Translate field name to Polish.
 */
function translateFieldName(field: string): string {
  return FIELD_NAMES[field] || field;
}

/**
 * Main utility function - handles any error type and returns user-friendly message.
 *
 * @example
 * try {
 *   await api.post('/api/clients', data);
 * } catch (error) {
 *   toast({ description: getErrorMessage(error) });
 * }
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.getFormattedMessage();
  if (error instanceof Error) return translateError(error.message, 0);
  if (typeof error === "string") return translateError(error, 0);
  return "Wystąpił nieoczekiwany błąd";
}

/**
 * Factory function to create ApiError from a Response object.
 * Parses the response body and extracts error details.
 */
export async function createApiError(response: Response): Promise<ApiError> {
  let errorData: ApiErrorResponse = {};

  try {
    const text = await response.text();
    if (text) {
      errorData = JSON.parse(text);
    }
  } catch {
    // Not JSON or empty - use status text
    errorData = { error: response.statusText };
  }

  const fieldErrors = extractFieldErrors(errorData);
  return new ApiError(response.status, errorData, fieldErrors);
}
